/**
 * Parsed view-graph params string.
 * The string format is the one the Rust view layer hands to the frontend in
 * `TigerViewElement.graph_tags_param`. It is also exactly what the
 * `get_time_graph_data` / `get_xy_graph_data` Tauri commands accept, so it
 * can be forwarded verbatim for the data fetch — this parser is for
 * populating the dialog window's per-series legend metadata.
 */
/**
 * Per-plot fields extracted from the params string for UI hydration.
 *
 * Intentionally excluded (the verbatim params string still carries them to the
 * backend fetch — they're just not surfaced for legend rendering):
 *   - `plot[i].multitime_start_time`, `multitime_end_time` — these are not actually
 *     emitted by the Rust backend; per-series time ranges are resolved client-side
 *     via getEventModelTimes(model)[relativeIndex].
 *   - `plot[i].x_model_name`, `y_model_name` (graph_type 8, GraphTigerXYModel)
 */
export interface ParsedPlot {
  tagName?: string;
  xTagName?: string;
  yTagName?: string;
  colour?: string;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  modelName?: string;
  multitimeMode?: string;
  multitimeModelEvent?: string;
  /** String-encoded integer; parse on the consumer side. */
  multitimeRelativeIndex?: string;
  /** Per-plot turbine state (e.g. "DEFAULT", "BASE_LOAD"). Absent for event graphs. */
  turbineState?: string;
}

export interface ParsedGraphParams {
  graphType?: number;
  sessionType?: string;
  numberOfPlots?: number;
  /** Top-level (not per-plot) `start_time`/`end_time`, in Tiger seconds as
   *  emitted by the historical-trend right-click menu. Used to hydrate the
   *  graph window's nav-bar time range. */
  startTime?: string;
  endTime?: string;
  plots: ParsedPlot[];
}

const KEY_TO_PROP: Record<string, keyof ParsedPlot> = {
  tag_name: "tagName",
  x_tag_name: "xTagName",
  y_tag_name: "yTagName",
  colour: "colour",
  start_date: "startDate",
  end_date: "endDate",
  start_time: "startTime",
  end_time: "endTime",
  model_name: "modelName",
  multitime_mode: "multitimeMode",
  multitime_model_event: "multitimeModelEvent",
  multitime_relative_index: "multitimeRelativeIndex",
  turbine_state: "turbineState",
};

export function parseGraphParams(input: string): ParsedGraphParams {
  const result: ParsedGraphParams = { plots: [] };
  if (!input) return result;

  const plotMap = new Map<number, ParsedPlot>();
  const plotKeyRe = /^plot\[(\d+)\]\.(.+)$/;

  for (const segment of input.split("|")) {
    const eq = segment.indexOf("=");
    if (eq <= 0) continue;
    const key = segment.slice(0, eq).trim();
    const value = segment.slice(eq + 1);

    if (key === "graph_type") {
      const n = parseInt(value, 10);
      if (Number.isFinite(n)) result.graphType = n;
      continue;
    }
    if (key === "session_type") {
      result.sessionType = value;
      continue;
    }
    if (key === "start_time") {
      result.startTime = value;
      continue;
    }
    if (key === "end_time") {
      result.endTime = value;
      continue;
    }
    if (key === "number_of_plots") {
      const n = parseInt(value, 10);
      if (Number.isFinite(n)) result.numberOfPlots = n;
      continue;
    }

    const m = plotKeyRe.exec(key);
    if (!m) continue;
    const idx = parseInt(m[1], 10);
    if (!Number.isFinite(idx)) continue;
    const fieldKey = m[2];
    const prop = KEY_TO_PROP[fieldKey];
    if (!prop) continue;

    let plot = plotMap.get(idx);
    if (!plot) {
      plot = {};
      plotMap.set(idx, plot);
    }
    plot[prop] = value;
  }

  // Emit plots in index order so legend rows match plot[0], plot[1], ...
  const indices = Array.from(plotMap.keys()).sort((a, b) => a - b);
  result.plots = indices.map((i) => plotMap.get(i)!);

  return result;
}
